import React from 'react'
import dynamic from 'next/dynamic'
import { FullScreenLoading } from 'src/components/full-screen-loading'
import { Insight, InsightComponentProps } from 'src/components/insights/types'
import { CarTrafficIcon } from 'src/components/insights/icons/car-traffic'
import { useTier } from 'src/hooks/use-tier'
import { INSIGHTS_TIER } from 'src/constants/insights-tier'
import { COMMANDAI_IDS_MAP } from 'src/constants/command-ai-ids'

import carTraffic1 from 'src/assets/images/insights-previews/car-traffic/car_traffic_1.gif'
import carTraffic1Placeholder from 'src/assets/images/insights-previews/car-traffic/car_traffic_1.png'
import carTraffic2 from 'src/assets/images/insights-previews/car-traffic/car_traffic_2.gif'
import carTraffic2Placeholder from 'src/assets/images/insights-previews/car-traffic/car_traffic_2.png'
import carTraffic3 from 'src/assets/images/insights-previews/car-traffic/car_traffic_3.gif'
import carTraffic3Placeholder from 'src/assets/images/insights-previews/car-traffic/car_traffic_3.png'
import { Colors } from 'src/components/theme'
import { InsightContainer } from 'src/v2/views/owner-reveal/insights/container'

const CarTrafficContent = dynamic(() => import('src/components/insights/car-traffic/content'), {
  loading: () => <FullScreenLoading />,
  ssr: false,
})

const InsightLocked = dynamic(() => import('src/components/insight-locked'), {
  loading: () => <FullScreenLoading />,
  ssr: false,
})

const CarTrafficPage: React.FC<InsightComponentProps> = (props) => {
  const { insight } = props
  const meetsTier = useTier(INSIGHTS_TIER['Car Traffic'])

  return (
    <InsightContainer insight={insight} color={Colors.blueDark}>
      <React.Suspense fallback={<FullScreenLoading />}>
        {!meetsTier ? <InsightLocked insight={insight} /> : <CarTrafficContent />}
      </React.Suspense>
    </InsightContainer>
  )
}

export const CarTraffic: Insight = {
  id: 'car-traffic',
  tabId: COMMANDAI_IDS_MAP.mapExplorer['car-traffic-insights-tab'],
  component: CarTrafficPage,
  name: 'Car Traffic',
  icon: CarTrafficIcon,
  supportedResultTypes: ['venue', 'address', 'area', 'alphamap-location'],
  linkTooltipFile: 'insights-link-photos',
  tier: INSIGHTS_TIER['Car Traffic'],
  paywall: {
    title: 'See Car Traffic Reports in Action',
    description: `Car Traffic isn't available on your current plan. However, you can view full functionality of this feature by viewing an unlocked location or upgrading your plan below.`,
    synopsis: [
      {
        image: carTraffic1.src,
        imagePlaceholder: carTraffic1Placeholder.src,
        title: 'Maximize Location Potential',
        description:
          'Harness car traffic insights to pinpoint high-visibility, high-traffic locations, boosting business exposure and customer access.',
      },
      {
        image: carTraffic2.src,
        title: 'Smart Investment Decisions',
        imagePlaceholder: carTraffic2Placeholder.src,
        description:
          'Leverage traffic data to assess property value and potential, making well-informed real estate investment choices.',
      },
      {
        image: carTraffic3.src,
        title: 'Informed Development Strategy',
        imagePlaceholder: carTraffic3Placeholder.src,
        description:
          'Use traffic insights for broader development strategies, from enhancing public infrastructure to guiding commercial and residential planning.',
      },
    ],
  },
}
